import { Client as Styletron } from 'styletron-engine-atomic';
import { Provider as StyletronProvider } from 'styletron-react';
import { LightTheme, BaseProvider } from 'baseui';
import { DatePicker, DatepickerProps } from 'baseui/datepicker';
import { useState } from 'react';
import useBriefContext from '../../../Hooks/Brief/useBriefContext';
import InputView from '../InputView';
import FormPage from '../../../Components/Form/Page/FormPage';
import { getReadableDuration } from './Utilities/duration';
import { DateRange, getBriefDates } from './Utilities/getBriefDates';
import {
  areSameDay,
  dateToMachineString,
  dateToReadableString,
  timestampToReadableString,
} from './Utilities/dateStrings';
import './BaseWeb/DatePicker.less'
import './DateTimeInput.less'
import DATE_PICKER_OVERRIDES from './BaseWeb/datePickerOverrides'
import TimePicker from './TimePicker';
import ValidationMessage from '../../../Components/Form/Inputs/ValidationMessage/ValidationMessage';

const engine = new Styletron();


// fromDate and toDate are captured as ISO8601 strings. These will be converted on brief submission
// fromTime and toTime are captured as timestamp strings of 'HH:mm:ss' (where ss is always 00)

interface EventTimeCaptionProps {
  dates: DateRange
  startTime: string
  endTime: string
}

const EventTimeCaption = ({dates, startTime, endTime}: EventTimeCaptionProps): JSX.Element => {
  const [startDate, endDate] = dates
  if (startDate === undefined || endDate === undefined || startTime === '' || endTime === '') {
    return <p className='time-caption empty' aria-live='polite'></p>
  }
  // trim the seconds from the timestamps
  const formattedStartTime = startTime && timestampToReadableString(startTime)
  const formattedEndTime = endTime && timestampToReadableString(endTime)

  if (areSameDay(dates)) {
    return (
      <p className='time-caption' aria-live='polite'>
        Your event lasts from{' '}<time>{formattedStartTime}</time>{' '}
        to{' '}<time>{formattedEndTime}</time>{' '}
        on{' '}<time dateTime={dateToMachineString(startDate)}>{
          dateToReadableString(startDate)
        }</time>{' '}
        ({getReadableDuration({dates: [startDate, endDate], startTime, endTime})}).
      </p>
    )
  }

  return (
    <p className='time-caption' aria-live='polite'>
      Your event starts on{' '}
      <time dateTime={`${dateToMachineString(startDate)}T${startTime}`}>
        {dateToReadableString(startDate)} at {formattedStartTime}
      </time>
      {' '}and ends on{' '}
      <time dateTime={`${dateToMachineString(endDate)}T${endTime}`}>
        {dateToReadableString(endDate)} at {formattedEndTime}
      </time>{' '}
        ({getReadableDuration({dates: [startDate, endDate], startTime, endTime})}).
    </p>
  )
}

const DateTimeInput: InputView = ({ path }): JSX.Element => {
  const { brief } = useBriefContext()
  const [dates, setDates] = useState(getBriefDates(brief))
  const [startTime, setStartTime] = useState(brief.fromTime ?? '09:00:00')
  const [endTime, setEndTime] = useState(brief.toTime ?? '17:00:00')
  const [invalidMessage, setInvalidMessage] = useState<string | null>(null)

  const onDatesChange: DatepickerProps['onChange'] = ({date}) => {
    const newDates = Array.isArray(date) ? date : [date]
    const [newStartDate, newEndDate] = newDates
    if (invalidMessage !== null) setInvalidMessage(null)

    setDates([newStartDate ?? undefined, newEndDate ?? undefined])
  }
  const onCalendarClose = (): void => {
    const [selectedStartDate, selectedEndDate] = dates
    if (selectedEndDate === undefined && selectedStartDate === undefined) {
      setInvalidMessage('Please enter valid dates.')
    } else if (selectedEndDate === undefined) { // default end date to start date
      setDates([selectedStartDate, selectedStartDate])
    }
  }

  const allAttributes = {
    fromTime: startTime,
    toTime: endTime,
    fromDate: dates[0]?.toISOString(),
    toDate: dates[1]?.toISOString(),
  }

  const isSameDay = dates[0]?.getDate() === dates[1]?.getDate();

  return (
    <FormPage
      metaTitle="Dates & times - HeadBox"
      heading='What are your preferred timings?'
      path={path}
      inputAttributes={allAttributes}
      className='date-time-page'>
      <StyletronProvider value={engine}>
        <BaseProvider theme={LightTheme}> {/* these wrappers are base-ui dependencies */}
          <ValidationMessage className='datepicker-wrapper' invalidMessage={invalidMessage}>
            <DatePicker
              value={dates}
              onChange={onDatesChange}
              onClose={onCalendarClose}
              minDate={new Date()}
              required
              range
              error={!!invalidMessage}
              separateRangeInputs
              aria-label='Date or dates'
              overrides={DATE_PICKER_OVERRIDES}
              />
          </ValidationMessage>
        </BaseProvider>
      </StyletronProvider>

      <div className='timings'>
        <label htmlFor='start-time-input'>
          Start time
          <TimePicker id='start-time-input' value={startTime} setValue={setStartTime} required/>
        </label>

          <label htmlFor='end-time-input'>
            End time
            <TimePicker 
              id='end-time-input' 
              value={endTime} 
              setValue={setEndTime} 
              baseValue={isSameDay ? startTime : undefined} 
              required
              />
          </label>
        </div>

      <EventTimeCaption dates={dates} startTime={startTime} endTime={endTime} />
    </FormPage>
  )
}

export default DateTimeInput
